import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

import TableLoader from "$components/table/table-loader.js";
import BulkActionBar from "$components/table/bulk-action-bar.js";
import FeedbackDialog from "src/pages/projects/potential/libs/feedback-dialog";

import { mapToTableData } from "src/pages/projects/existing/list.js";
import { ClientDetailsStore } from "$stores/client-details.js";
import { AppContextStore } from "$stores/app-context.js";
import { mapByKey } from "$utils/mapping.js";
import { usePaginatedStoreWrapper } from "$utils/hooks/usePaginatedStore.js";
import { ClientPotentialProjectsStore, fetchClientPotentialProjects, setFilters, setFlagged, setHiddenColumns, setPageIndex, setPagingInfo, setPerPage, setSelectedData, setSort } from "$stores/clientPotentialProjects.js";
import { buildDefaultColumns } from "src/pages/projects/potential/libs/columns";
import { dialogConfirm } from "$stores/modal.js";
import api from "$services/api.js";
import { getTerm } from "$stores/dictionary";

const ClientPotentialProjects = () => {
    const navigate = useNavigate();
    const appInsights = useAppInsightsContext();
    const { filterMenu, visibleColumns } = AppContextStore.useState((f) => {
        return { filterMenu: mapByKey(f.filterMenu, "apiKey") || {}, visibleColumns: f.siteConfig?.potentialProjectListColumns || null };
    });

    const [clientProjectsColumns, setClientProjectsColumns] = useState([]);

    const { hasSubClients, clientDetails } = ClientDetailsStore.useState((s) => {
        return { hasSubClients: s.hasSubClients, clientDetails: s.clientDetails };
    });
    const { clientPotentialProjects, filters, globalFilterRanges, hiddenColumns, isLoading, isSortAsc, pageCount, pageIndex, pageSize, selectedData, sort } = usePaginatedStoreWrapper(ClientPotentialProjectsStore, {
        fetchList: fetchClientPotentialProjects,
        setPagingInfo: setPagingInfo,
        overrideGlobalFilters: { client: [{ value: clientDetails.id, text: clientDetails.name }] },
        suppressGlobalFilters: true,
    });

    const sendFlags = async () => {
        if (selectedData.length <= 0) {
            return false;
        }

        const confirmUpdate = await dialogConfirm({
            data: {},
            Component: FeedbackDialog,
            settings: { type: "confirm" },
        });

        const flagData = {
            projects: selectedData.map((s) => {
                return {
                    key: s.projectKey,
                    id: s.id,
                };
            }),
            ...confirmUpdate,
        };

        if (confirmUpdate) {
            const result = await api.put("/api/projects/potential/bulk/user-flag", flagData);
            if (result) {
                setFlagged(selectedData.map((s) => s.id));
                setSelectedData("clear");
            }
        }
    };

    useEffect(() => {
        // Filter out clientGroupName from the default potential project columns
        const defaultColumns = buildDefaultColumns({ settings: { visibleColumns }, globalFilterRanges, filterOptions: filterMenu, updateSelectedList: setSelectedData }).filter((c) => c.accessorKey !== "clientGroupName");
        setClientProjectsColumns(hasSubClients ? defaultColumns : defaultColumns.filter((c) => c.accessorKey !== "clientName"));
    }, [hasSubClients, filterMenu, globalFilterRanges, visibleColumns]);

    const rowClick = (row) => {
        navigate(`/project/${row.clientId}/${row.projectTypeId}/potential`);
        appInsights.trackEvent(
            { name: `Navigation - ${getTerm("potentialProject")} List` },
            {
                label: row.projectKey,
                page: document.title.replace(" - Orgaimi", ""),
            }
        );
    };
    const data = clientPotentialProjects.map(mapToTableData);
    return (
        <>
            <BulkActionBar selectedCount={selectedData.length} onFlag={sendFlags} />
            <TableLoader
            allowFilter={true}
            columns={clientProjectsColumns}
            containerClass="project-list"
            data={data}
            filters={filters}
            hiddenColumns={hiddenColumns}
            isLoading={isLoading}
            isSortAsc={isSortAsc}
            onRowClick={rowClick}
            pageCount={pageCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            selectedData={selectedData}
            setFilters={setFilters}
            setHiddenColumns={setHiddenColumns}
            setPageIndex={setPageIndex}
            setPerPage={setPerPage}
            setSort={setSort}
            sort={sort}
        />
        </>
    );
};

export default ClientPotentialProjects;
